/**
 * computer-use-tool.ts — Anthropic Computer Use tools for the local AI-SDK eval
 * path, backed by the headless-Chromium harness (PR 3).
 *
 * Browser-rendered MCP App eval PR 4. Registers two AI SDK tools:
 *   - `computer` — the Anthropic provider-native computer tool
 *     (`anthropic.tools.computer_20250124` / `computer_20251124`). Including the
 *     factory in the tool map is what makes the AI SDK Anthropic provider
 *     auto-attach the matching beta header (`computer-use-2025-01-24` /
 *     `-11-24`) — no providerOptions / extraHeaders plumbing. Its `execute`
 *     drives the harness; `toModelOutput` turns the implementation result into a
 *     model-visible screenshot + a summary of any widget-initiated tools/call.
 *   - `finish_widget` — a regular AI SDK tool the model calls to dismiss a
 *     rendered widget when it's done interacting.
 *
 * Eligibility is capability-based, not Claude-only: the wire-format `computer`
 * tool (the form every production path uses) is an ordinary function tool any
 * vision + tool-calling model can drive — see
 * `model-capabilities.ts#modelSupportsComputerUse`. `resolveComputerUseToolVersion`
 * remains the offline fast path for mapped Claude ids and the version picker
 * for the provider-native form.
 */

import { tool, type ToolSet } from "ai";
import { anthropic } from "@ai-sdk/anthropic";
import { z } from "zod";
import type {
  BrowserActionResult,
  BrowserActionSpec,
  McpAppBrowserHarness,
  WidgetToolCall,
} from "./mcp-app-browser-harness";
import { logger } from "./logger";

export type ComputerUseToolVersion = "20250124" | "20251124";

/**
 * Model id → computer-tool version. A single source of truth so the unit test
 * can enumerate every entry against the AI SDK provider-tool registry and catch
 * drift on SDK upgrades. Matched by longest id prefix (so dated/suffixed ids
 * like `claude-sonnet-4-5-20250929` resolve correctly). Updated whenever a new
 * Claude model with Computer Use ships.
 */
export const COMPUTER_USE_TOOL_VERSIONS: Record<
  string,
  ComputerUseToolVersion
> = {
  // computer_20251124 (Opus 4.5+ generation / newest)
  "claude-opus-4-8": "20251124",
  "claude-opus-4-7": "20251124",
  "claude-opus-4-6": "20251124",
  "claude-sonnet-4-7": "20251124",
  "claude-sonnet-4-6": "20251124",
  "claude-haiku-4-6": "20251124",
  // computer_20250124 (4.x generation)
  "claude-sonnet-4-5": "20250124",
  "claude-haiku-4-5": "20250124",
  "claude-opus-4-5": "20250124",
  "claude-sonnet-4": "20250124",
  "claude-opus-4": "20250124",
  "claude-haiku-4": "20250124",
};

// Longest-prefix order so e.g. `claude-opus-4-8` wins over `claude-opus-4`.
const VERSION_KEYS_BY_LENGTH = Object.keys(COMPUTER_USE_TOOL_VERSIONS).sort(
  (a, b) => b.length - a.length
);

function normalizeModelId(
  model: string | { id?: string; modelId?: string } | null | undefined
): string | undefined {
  const raw =
    typeof model === "string"
      ? model
      : model?.id ?? model?.modelId ?? undefined;
  if (!raw) return undefined;
  // Strip a provider prefix (`anthropic/`, `anthropic.`), lowercase, and
  // convert version dots to hyphens so dotted MCPJam ids
  // (`anthropic/claude-haiku-4.5` — the default eval model) match the
  // hyphenated keys in COMPUTER_USE_TOOL_VERSIONS (`claude-haiku-4-5`).
  return raw
    .toLowerCase()
    .replace(/^anthropic[./]/, "")
    .replace(/\./g, "-");
}

/**
 * Resolve the computer-tool version for a driver model, or `null` if the model
 * doesn't support Computer Use (non-Claude, or an unmapped Claude id) — in
 * which case the caller must not advertise the `computer` / `finish_widget`
 * tools.
 */
export function resolveComputerUseToolVersion(
  model: string | { id?: string; modelId?: string } | null | undefined
): ComputerUseToolVersion | null {
  const id = normalizeModelId(model);
  if (!id) return null;
  for (const key of VERSION_KEYS_BY_LENGTH) {
    if (id === key || id.startsWith(`${key}-`)) {
      return COMPUTER_USE_TOOL_VERSIONS[key];
    }
  }
  return null;
}

/** Broad structural type covering both versions' action inputs. */
export interface ComputerActionInput {
  action: string;
  coordinate?: [number, number];
  start_coordinate?: [number, number];
  text?: string;
  duration?: number;
  scroll_amount?: number;
  scroll_direction?: "up" | "down" | "left" | "right";
  region?: [number, number, number, number];
}

/** Implementation output of the `computer` tool (not model-visible directly). */
export interface ComputerImplOutput {
  screenshotBase64?: string;
  widgetToolCalls: WidgetToolCall[];
  action: ComputerActionInput;
  elapsedMs: number;
  /** Harness note (e.g. "step_budget_exceeded", "no_rendered_widget"). */
  note?: string;
}

type ComputerModelOutput = {
  type: "content";
  value: Array<
    | { type: "text"; text: string }
    | { type: "image-data"; data: string; mediaType: string }
  >;
};

/** Map the AI SDK computer action to the harness's supported action subset. */
export function mapToBrowserAction(
  input: ComputerActionInput
): BrowserActionSpec {
  switch (input.action) {
    case "left_click":
    case "double_click":
    case "right_click":
    case "mouse_move":
      return { action: input.action, coordinate: input.coordinate };
    case "triple_click":
      // No triple-click in the harness; double_click is the closest analogue.
      return { action: "double_click", coordinate: input.coordinate };
    case "type":
      return { action: "type", text: input.text };
    case "key":
      return { action: "key", text: input.text };
    case "scroll":
      return {
        action: "scroll",
        coordinate: input.coordinate,
        scrollAmount: input.scroll_amount,
        scrollDirection: input.scroll_direction,
      };
    case "wait":
      return { action: "wait", duration: input.duration };
    case "screenshot":
    case "cursor_position":
      return { action: "screenshot" };
    default:
      // hold_key, left_mouse_down/up, left_click_drag, middle_click, zoom, …
      // are not modeled by the harness — return current state via a screenshot.
      return { action: "screenshot" };
  }
}

function detectImageMediaType(base64: string): string {
  // JPEG base64 begins with "/9j/"; PNG with "iVBOR".
  return base64.startsWith("/9j/") ? "image/jpeg" : "image/png";
}

/** Human-readable summary of widget-initiated tools/call for the model. */
export function summarizeWidgetToolCalls(
  calls: WidgetToolCall[]
): string | null {
  if (!calls.length) return null;
  const parts = calls.map((c) => {
    const argStr = Object.entries(c.args ?? {})
      .map(([k, v]) => `${k}=${formatArg(v)}`)
      .join(", ");
    const status = c.ok ? "OK" : `ERROR(${c.error ?? "unknown"})`;
    return `${c.name}(${argStr}) → ${status}`;
  });
  return `During this action the widget invoked: ${parts.join("; ")}`;
}

function formatArg(v: unknown): string {
  if (typeof v === "string") return v.length > 40 ? `${v.slice(0, 37)}…` : v;
  if (v === null || typeof v !== "object") return String(v);
  return "…";
}

/** Translate the implementation output into model-visible content parts. */
export function toComputerModelOutput(
  output: ComputerImplOutput
): ComputerModelOutput {
  const value: ComputerModelOutput["value"] = [];
  if (output.screenshotBase64) {
    value.push({
      type: "image-data",
      data: output.screenshotBase64,
      mediaType: detectImageMediaType(output.screenshotBase64),
    });
  }
  const summary = summarizeWidgetToolCalls(output.widgetToolCalls);
  if (summary) value.push({ type: "text", text: summary });
  if (output.note) {
    value.push({ type: "text", text: `[harness: ${output.note}]` });
  }
  if (value.length === 0) {
    value.push({ type: "text", text: "No visible change." });
  }
  return { type: "content", value };
}

export interface BuildComputerUseToolsOptions {
  /** Anthropic provider-native tool version. Required when `wireFormat` is
   *  false (it picks the factory + beta header); wire-format callers may omit
   *  it — the hand-rolled schema is version- and provider-independent, which
   *  is what lets non-Claude drivers use the tool. */
  version?: ComputerUseToolVersion;
  harness: McpAppBrowserHarness;
  /** The tool-call id of the widget that `computer` actions target (the active
   *  rendered widget). Returns null when no widget is mounted. */
  getActiveToolCallId: () => string | null;
  /** Viewport == screenshot pixel space == the model's coordinate space. */
  viewport: { width: number; height: number };
  /**
   * PR 6b: fires after every `harness.executeAction()` returns. Receives the
   * raw `BrowserActionResult` plus the toolCallId of the active widget the
   * action targeted. The eval runner uses this to collect
   * `browserInteractionSteps` for persistence. It does NOT fire on the
   * no-widget short-circuit (that path is already represented in
   * `widgetRenderObservations` + the model loop), and never touches the
   * model-side `toModelOutput` path.
   */
  onAction?: (
    result: BrowserActionResult,
    context: { toolCallId: string }
  ) => void;
  /**
   * PR 14 (hosted-path Computer Use): emit the `computer` tool as a REGULAR
   * AI SDK tool with an explicit JSON-serializable input schema instead of
   * the Anthropic provider-native factory. The hosted backend paths
   * serialize the tool map to flat `{name, description, inputSchema}` defs
   * for the Convex `/stream` request (`serializeToolsForConvex`), and the
   * provider-native tool's lazy schema serializes to an EMPTY object there —
   * the model would see a `computer` tool it can't call. Wire format trades
   * the provider beta header (OpenRouter exposes function tools only) for a
   * faithful hand-rolled schema covering exactly the harness-supported
   * action set. Local AI-SDK paths keep the default (provider-native).
   */
  wireFormat?: boolean;
}

/**
 * Actions the wire-format `computer` tool advertises. Mirrors the Anthropic
 * native action vocabulary restricted to what the harness implements (plus
 * `triple_click`, which maps to the closest analogue) — advertising actions
 * that silently degrade to screenshots (drag, hold_key, …) would mislead the
 * model on a function-tool surface that lacks the native harness prompt.
 */
const WIRE_FORMAT_ACTIONS = [
  "screenshot",
  "left_click",
  "double_click",
  "triple_click",
  "right_click",
  "mouse_move",
  "type",
  "key",
  "scroll",
  "wait",
] as const;

function buildWireFormatComputerInputSchema(viewport: {
  width: number;
  height: number;
}) {
  return z.object({
    action: z
      .enum(WIRE_FORMAT_ACTIONS)
      .describe(
        "The action to perform on the rendered widget. Take a screenshot " +
          "first to see the current state before clicking or typing."
      ),
    coordinate: z
      .array(z.number().int().min(0))
      .min(2)
      .max(2)
      .optional()
      .describe(
        `[x, y] pixel coordinate for click / mouse_move / scroll actions. ` +
          `x is in [0, ${viewport.width}), y is in [0, ${viewport.height}).`
      ),
    text: z
      .string()
      .optional()
      .describe(
        "Text to type (for `type`), or the key / key-combination to press " +
          "(for `key`, e.g. 'Return', 'Tab', 'ctrl+a')."
      ),
    duration: z
      .number()
      .min(0)
      .optional()
      .describe("Seconds to pause (for `wait`)."),
    scroll_amount: z
      .number()
      .int()
      .min(0)
      .optional()
      .describe("Number of scroll wheel clicks (for `scroll`)."),
    scroll_direction: z
      .enum(["up", "down", "left", "right"])
      .optional()
      .describe("Direction to scroll (for `scroll`)."),
  });
}

/**
 * Build the `computer` (Anthropic provider-native) + `finish_widget` tools.
 * Including the resolved provider-tool factory in the returned set is what makes
 * the AI SDK Anthropic provider attach the matching beta header.
 */
export function buildComputerUseTools(
  opts: BuildComputerUseToolsOptions
): ToolSet {
  const { harness, getActiveToolCallId, viewport } = opts;

  const execute = async (
    input: ComputerActionInput
  ): Promise<ComputerImplOutput> => {
    const toolCallId = getActiveToolCallId();
    if (!toolCallId) {
      return {
        widgetToolCalls: [],
        action: input,
        elapsedMs: 0,
        note: "no_rendered_widget",
      };
    }
    const result = await harness.executeAction({
      toolCallId,
      action: mapToBrowserAction(input),
    });
    try {
      opts.onAction?.(result, { toolCallId });
    } catch (err) {
      // Engine-callback contract: a buggy emitter must not crash the agent
      // loop. Surface and continue (mirrors the stream handler's onToolCall).
      logger.warn("[computer-use] onAction callback threw", {
        error: err instanceof Error ? err.message : String(err),
      });
    }
    return {
      screenshotBase64: result.screenshotBase64,
      widgetToolCalls: result.widgetToolCalls,
      action: input,
      elapsedMs: result.elapsedMs,
      note: result.note,
    };
  };

  const toModelOutput = ({ output }: { output: ComputerImplOutput }) =>
    toComputerModelOutput(output);

  const factoryArgs = {
    displayWidthPx: viewport.width,
    displayHeightPx: viewport.height,
    execute,
    toModelOutput,
  };

  // Wire format (hosted backend paths): a regular function tool with an
  // explicit, JSON-serializable schema. Same execute / toModelOutput
  // implementation — only the model-facing surface differs (function tool
  // vs. provider-native tool type + beta header). Built as an object literal
  // rather than via `tool()`: the helper is a pure inference identity, and
  // its overloads can't unify the zod-v4 schema with `toModelOutput`'s
  // output type. The consumers are duck-typed (`serializeToolsForConvex`
  // reads `inputSchema`, `executeToolCallsFromMessages` reads `execute` /
  // `toModelOutput`), so the literal is a first-class tool.
  let computer: ToolSet[string];
  if (opts.wireFormat) {
    computer = {
      description:
        `A virtual ${viewport.width}x${viewport.height} browser viewport ` +
        "showing the currently rendered MCP App widget. Use it to look at " +
        "the widget (screenshot) and interact with it (click, type, " +
        "scroll). Coordinates are pixels with (0, 0) at the top-left. " +
        "Always take a screenshot first to see the widget before " +
        "interacting, and after each action to verify its effect. When " +
        "you are done interacting with the widget, call `finish_widget`.",
      inputSchema: buildWireFormatComputerInputSchema(viewport),
      // The wire schema types `coordinate` as number[] (tuple constraints
      // ride as minItems/maxItems so the JSON round-trips through the
      // backend's schema reconstruction); the impl type narrows it.
      execute: (input: ComputerActionInput) => execute(input),
      toModelOutput,
    } as unknown as ToolSet[string];
  } else {
    if (!opts.version) {
      throw new Error(
        "buildComputerUseTools: `version` is required for the provider-native " +
          "computer tool — only wire-format callers may omit it"
      );
    }
    computer =
      opts.version === "20251124"
        ? anthropic.tools.computer_20251124(factoryArgs)
        : anthropic.tools.computer_20250124(factoryArgs);
  }

  const finish_widget = tool({
    description:
      "Call this when you are done interacting with the currently rendered " +
      "MCP App widget. Dismisses the widget so the conversation can continue.",
    inputSchema: z.object({
      toolCallId: z
        .string()
        .describe("The tool call id of the rendered widget to dismiss."),
    }),
    execute: async ({ toolCallId }: { toolCallId: string }) => {
      // Dismiss the widget that is actually mounted (the harness keeps at most
      // one, exposed via the same getActiveToolCallId the `computer` tool
      // targets) rather than blindly the id the model passed. A stale or
      // mistaken toolCallId must not report success while leaving the live
      // widget mounted — otherwise Computer Use keeps targeting a widget the
      // model believes is gone.
      const active = getActiveToolCallId();
      if (!active) {
        return { ok: false as const, dismissed: null, requested: toolCallId };
      }
      await harness.dismissWidget(active);
      return { ok: true as const, dismissed: active, requested: toolCallId };
    },
  });

  return { computer, finish_widget };
}
